import { FC, useCallback, useEffect, useState } from "react";
import { useAtomValue } from "jotai";

import {
  BinMaterialEnum,
  BinStatusEnum,
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { i18n } from "@/i18n";
import { useTranslator } from "@/i18n/useTranslator";
import { formatBinSize } from "@/src/utils/strings";

import { TaskApi } from "../data/api/TaskApi";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useBinsListQuery } from "../data/queries/binQueries";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { getBinMaterialOptions } from "../utils/bin";
import { generateFirebaseTimestamp } from "../utils/date";
import { logger } from "../utils/logger";
import { DriverScreenWithSelectBox } from "./DriverScreenWithSelectBox";
import { DriverStepScreenWrapper } from "./DriverStepScreenWrapper";

const BIN_MATERIAL_OPTIONS = getBinMaterialOptions({
  emptyOptionLabel: i18n.t("driver_filling_bin_contents.input_placeholder"),
});

const DriverFillingBinContents: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const [shouldContactOffice, setShouldContactOffice] = useState(false);
  const [isAssigningBin, setIsAssigningBin] = useState(false);

  const isWaitAndLoadBinTask = taskId === ServiceTaskTypeEnum.WAITANDLOADBIN;
  const isPickUpBinTask = taskId === ServiceTaskTypeEnum.PICKUPBIN;
  const isExchangeBinTask = taskId === ServiceTaskTypeEnum.EXCHANGEBIN;

  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const binIdentifier = isWaitAndLoadBinTask
    ? serviceData?.assignedBin?.identifier
    : taskData?.connectedTask?.assignedBin?.identifier;
  const binSize = formatBinSize(
    isWaitAndLoadBinTask
      ? serviceData?.assignedBin?.size
      : taskData?.connectedTask?.assignedBin?.size,
  );

  const { isLoading: isLoadingBinsOnSite, refetch: refetchBinsOnSite } =
    useBinsListQuery({
      filters: {
        deleted: false,
        status: BinStatusEnum.ASSIGNED,
        location: serviceData?.address,
      },
      isEnabled: false,
    });

  const [selectedBinContent, setSelectedBinContent] =
    useState<BinMaterialEnum | null>(serviceData?.binMaterials ?? null);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const searchAndAssignBin = useCallback(async () => {
    const binsOnSite = await refetchBinsOnSite();

    if (binsOnSite?.data?.length) {
      if (binsOnSite?.data?.length > 1) {
        setShouldContactOffice(true);
      } else {
        const bin = binsOnSite.data[0];

        if (bin.currentJob && bin.currentService) {
          try {
            setIsAssigningBin(true);

            //Get the task data from the original drop bin task
            const dropBinTaskData = await TaskApi.getTask({
              jobId: bin.currentJob,
              serviceId: bin.currentService,
              taskId: ServiceTaskTypeEnum.DROPBIN,
            });

            if (taskData) {
              updateTask({
                jobId,
                serviceId,
                taskId,
                connectedTask: {
                  assignedBin: {
                    assignedAt: generateFirebaseTimestamp(),
                    ...bin,
                  },
                  ...dropBinTaskData,
                },
              });
            }

            setIsAssigningBin(false);
          } catch (error) {
            logger.error("Error assigning bin", error);
            setIsAssigningBin(false);
          }
        }
      }
    }
  }, [jobId, refetchBinsOnSite, serviceId, taskData, taskId, updateTask]);

  useEffect(() => {
    if (!binIdentifier && (isPickUpBinTask || isExchangeBinTask)) {
      void searchAndAssignBin();
    }
  }, [binIdentifier, isExchangeBinTask, isPickUpBinTask, searchAndAssignBin]);

  const onNextClick = () => {
    if (selectedBinContent === null) return;

    const driverFlowStatus = isDriverReviewingTask
      ? TaskDriverStatusEnum.TASK_REVIEW
      : TaskDriverStatusEnum.TAKING_PHOTO_BIN_CONTENTS;

    updateService({
      jobId,
      serviceId,
      binMaterials: selectedBinContent,
      driverFlowStatus,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTO_BIN_CONTENTS;
  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.DRIVER_PICK_UP_BIN_ON_SITE;

  const goToPreviousStep = () => {
    const driverFlowStatus = isWaitAndLoadBinTask
      ? TaskDriverStatusEnum.WAIT_BIN_FILLED
      : TaskDriverStatusEnum.DRIVER_PICK_UP_BIN_ON_SITE;

    updateService({
      jobId,
      serviceId,
      driverFlowStatus,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const handleOnSelect = (option: string) => {
    setSelectedBinContent(option as BinMaterialEnum);
  };

  if (shouldContactOffice) {
    return (
      <DriverStepScreenWrapper
        jobId={jobId}
        serviceId={serviceId}
        taskId={taskId}
        title="driver_filling_bin_contents.contact_office"
        isNextButtonDisabled
        onNextButtonClick={() => null}
      />
    );
  }

  return (
    <DriverScreenWithSelectBox
      jobId={jobId}
      isLoading={isLoadingBinsOnSite || isAssigningBin}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_bin_contents.title"
      titlei18nOptions={{
        stepNumber: isPickUpBinTask ? 2 : 6,
        binIdentifier,
        binSize,
      }}
      selectBoxProps={{
        items: BIN_MATERIAL_OPTIONS,
        selectedValue: selectedBinContent ?? "",
        onSelect: handleOnSelect,
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedBinContent,
        previousButtonTitle: isWaitAndLoadBinTask
          ? "driver_filling_bin_contents.wait_bin_filled"
          : "driver_filling_bin_contents.pick_up_bin",
        previousButtonTitlei18nOptions: {
          binIdentifier,
          binSize,
        },
        onPreviousButtonClick:
          isDriverReviewingTask || isPickUpBinTask
            ? undefined
            : goToPreviousStep,
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingBinContents };
